import { Component, Inject } from "@angular/core";
import { CareerValuation } from "@common/ADAPT.Common.Model/organisation/career-valuation";
import { CareerValuationCategoryValue } from "@common/ADAPT.Common.Model/organisation/career-valuation-category-value";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { EntityPersistentService } from "@common/lib/data/entity-persistent.service";
import { PersistableDialog } from "@common/lib/data/persistable-dialog.decorator";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogWithDiscardConfirmationComponent } from "@common/ux/adapt-common-dialog/base-dialog-with-discard-confirmation.component/base-dialog-with-discard-confirmation.component";
import { DateFormats } from "@common/ux/date-formats";
import { EMPTY } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { ItemUtilities } from "../../kanban/items/item-utilities";
import { KanbanService } from "../../kanban/kanban.service";
import { KanbanUiService } from "../../kanban/kanban-ui.service";
import { LinkService } from "../../link/link.service";
import { OrgCommonCareerValuationService } from "../org-common-career-valuation.service";

export interface IEditCareerValuationDialogData {
    careerValuation: CareerValuation;
    previousCareerValuation?: CareerValuation;
    showingIdealRating?: boolean;
}

@Component({
    selector: "adapt-edit-cvt-dialog",
    templateUrl: "./edit-cvt-dialog.component.html",
    styleUrls: ["./edit-cvt-dialog.component.scss"],
    standalone: false,
})
@PersistableDialog("EditCareerValuationDialog", (injector) => injector.get(OrgCommonCareerValuationService).promiseToWaitUntilOrganisationInitialised())
export class EditCareerValuationDialogComponent extends BaseDialogWithDiscardConfirmationComponent<IEditCareerValuationDialogData, CareerValuation> {
    public readonly dialogName = "EditCareerValuationDialog";
    public title: string;

    public careerValuation: CareerValuation;

    public totalIdeal: number;
    private totalIdealUpdater = this.createThrottledUpdater<number>((totalIdeal) => this.totalIdeal = totalIdeal);

    public showingIdealRating: boolean;
    public allowCopyPrevious: boolean;

    private previousCareerValuation?: CareerValuation;

    public dateFormat = DateFormats.globalize.short;

    public showQuestionArray: boolean[] = [];

    public saveButtonDisabled = false;
    public actualButtonDisabled = false;
    public hasEditableBoard = false;

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public data: IEditCareerValuationDialogData,
        protected commonDataService: CommonDataService,
        private kanbanUiService: KanbanUiService,
        private linkService: LinkService,
        private entityPersistentService: EntityPersistentService,
        private orgCommonCareerValuationUiService: OrgCommonCareerValuationService,
        kanbanService: KanbanService,
    ) {
        super();

        this.careerValuation = data.careerValuation;
        this.previousCareerValuation = data.previousCareerValuation;
        this.totalIdeal = 0;
        this.showingIdealRating = true;
        this.careerValuation.categoryValues.forEach((cv) => this.setQuestionsVisibility(cv));
        // categoryValues should be in order of the category ordinal but this is not guaranteed when restoring
        this.careerValuation.categoryValues.sort((a, b) => a.category.ordinal - b.category.ordinal);

        const addMode = this.careerValuation.entityAspect.entityState.isAdded();
        this.title = (addMode ? "Add " : "Edit ") + "Career Valuation";

        this.activate();

        this.allowCopyPrevious = addMode && !!this.previousCareerValuation;

        linkService.linkedItemChanged$(() => this.careerValuation.links).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.careerValuation.links.sort(ItemUtilities.getItemLinkStatusAndItemCodeSortComparator()));

        kanbanService.hasAnyEditableBoard().pipe(
            this.takeUntilDestroyed(),
        ).subscribe((hasEditableBoard) => this.hasEditableBoard = hasEditableBoard);
    }

    @Autobind
    public saveAndClose() {
        return this.orgCommonCareerValuationUiService.saveEntities(this.entitiesToConfirm).pipe(
            tap(() => this.resolve(this.careerValuation)),
        );
    }

    public get entitiesToConfirm() {
        const entitiesToConfirm: any[] = [
            this.careerValuation,
            ...this.careerValuation.categoryValues,
            ...this.careerValuation.links,
        ];

        return entitiesToConfirm;
    }

    @Autobind
    public setPageChange() {
        this.showingIdealRating = !this.showingIdealRating;
        this.performPostPageChanged();
    }

    @Autobind
    public handleIdealCategoryValueChange() {
        this.totalIdealUpdater.next(this.careerValuation.extensions.idealTotal);
        this.careerValuation.categoryValues.forEach((cv) => {
            if (cv.actualValue > cv.idealValue) {
                cv.actualValue = cv.idealValue;
            }
        });

        this.handleEntityChanges();
    }

    @Autobind
    public toggleAccordion(categoryValue: CareerValuationCategoryValue) {
        this.showQuestionArray[categoryValue.careerValuationCategoryId] = !this.showQuestionArray[categoryValue.careerValuationCategoryId];
    }

    @Autobind
    public onContentsChange(entity: CareerValuation | CareerValuationCategoryValue, content: string) {
        entity.notes = content;
        this.handleEntityChanges();
    }

    @Autobind
    public handleEntityChanges() {
        const a = this.careerValuation.links.some((link) => link.entityAspect.hasValidationErrors);
        this.saveButtonDisabled = this.careerValuation.entityAspect.hasValidationErrors
            || !this.careerValuation.extensions.isValid()
            || this.careerValuation.categoryValues.some((cv) => cv.entityAspect.hasValidationErrors || !cv.extensions.isEditorValid)
            || a;

        this.actualButtonDisabled = this.careerValuation.extensions.idealTotal !== 100;
    }

    public setDate(date: string | number | Date | null) {
        this.careerValuation.creationDate = date as Date;
    }

    @Autobind
    private activate() {
        if (this.data.showingIdealRating !== undefined) {
            this.showingIdealRating = this.data.showingIdealRating!;
        } else {
            this.showingIdealRating = true;
        }

        this.performPostPageChanged();
        this.handleIdealCategoryValueChange();
    }

    @Autobind
    private performPostPageChanged() {
        this.handleEntityChanges();

        // move the scroll back to the top (as the dialog is quite long)
        window.scrollTo(0, 0);

        // record this so that it can be persisted if entities changed
        this.data.showingIdealRating = this.showingIdealRating;
    }

    private setQuestionsVisibility(categoryValue: CareerValuationCategoryValue) {
        this.showQuestionArray[categoryValue.careerValuationCategoryId] = false;
    }

    public copyPreviousValuationIdealValues() {
        if (this.previousCareerValuation) {
            this.careerValuation.categoryValues.forEach((cv) => this.copyIdealValue(cv));
        }
    }

    private copyIdealValue(currentValue: CareerValuationCategoryValue) {
        if (this.previousCareerValuation) {
            // Copy from previous if category id matches and it's not null
            for (const previousValue of this.previousCareerValuation.categoryValues) {
                if (currentValue.careerValuationCategoryId === previousValue.careerValuationCategoryId && previousValue.idealValue !== null) {
                    currentValue.idealValue = previousValue.idealValue;
                    break;
                }
            }
        }
    }

    @Autobind
    public addAction() {
        return this.kanbanUiService.createItem(undefined, { forceAllBoards: true }).pipe(
            switchMap((item: Item) => this.linkService.createLinkForCareerValuation(this.careerValuation, item.itemId)),
            switchMap((link) => {
                // manually persist the link, otherwise will only be persisted if other cv property changes
                this.entityPersistentService.persistEntity(link);
                return this.careerValuation.careerValuationId > 0 ? this.linkService.saveEntities(link) : EMPTY;
            }),
        );
    }

}
